import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageShell, SubpageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/data/posts";
import { usePosts } from "@/hooks/useContent";
import { useSeo } from "@/hooks/useSeo";

export default function BlogIndexPage() {
  useSeo({
    title: "Small Business Tax & Accounting Blog | Rosevale Accountants, Bristol & Bath",
    description: "Practical guides on UK budgets, tax, payroll and running a small business — written by Rosevale, small business accountants in Bristol & Bath.",
    path: "/blog",
  });

  const POSTS = usePosts();
  const posts = [...POSTS].sort((a, b) => b.date.localeCompare(a.date));
  const [featured, ...rest] = posts;

  return (
    <PageShell testid="blog-index-page">
      <SubpageHero
        eyebrow="From the blog"
        title="Practical guides to help your business thrive."
        lead="Budgets, tax changes, payroll and the practical side of running a business — explained by people who do this every day."
        crumbs={[{ label: "Blog" }]}
      />

      <section className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-card-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 rounded-3xl bg-white border border-[#103080]/10 p-5 sm:p-6 lift-shadow mb-8"
            >
              <div className="overflow-hidden rounded-2xl">
                <img src={featured.image} alt="" className="w-full h-64 lg:h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
              </div>
              <div className="flex flex-col justify-center py-4 lg:pr-6">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#0090D0] font-medium">Latest · {formatDate(featured.date)} · {featured.readTime}</p>
                <h2 className="font-serif font-semibold text-[#103080] text-3xl sm:text-4xl leading-tight mt-4 group-hover:text-[#0090D0] transition-colors duration-300">{featured.title}</h2>
                <p className="mt-4 text-[#0F172A]/65 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#103080]">Read the post <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
              </div>
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group h-full flex flex-col rounded-3xl bg-white border border-[#103080]/10 p-4 lift-shadow">
                  <div className="overflow-hidden rounded-2xl">
                    <img src={p.image} alt="" className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45">{formatDate(p.date)} · {p.readTime}</p>
                    <h3 className="font-serif font-semibold text-[#103080] text-xl leading-snug mt-2 group-hover:text-[#0090D0] transition-colors duration-300">{p.title}</h3>
                    <p className="mt-3 text-sm text-[#0F172A]/60 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
